import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Location } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { ActivatedRoute, Router } from '@angular/router';
import { BeneficiaryGroupEditComponent } from '@app/beneficiary-group/beneficiary-group-edit/beneficiary-group-edit.component';
import { MoneybarContainerComponent } from '@app/funding/moneybar-container/moneybar-container.component';
import { TotalFunding } from '@app/shared/funding/total-funding.model';
import { Permissions } from '@core/constants/permissions';
import { NotificationService } from '@core/services/notification.service';
import { BeneficiaryGroup } from '@shared/beneficiary/beneficiary-group';
import { BeneficiaryGroupApi } from '@shared/beneficiary/beneficiary-group-api.service';
import { Moneybar } from '@shared/funding/moneybar';
import { Setting } from '@shared/setting/setting';
import { SettingApi } from '@shared/setting/setting-api.service';
import { Moment } from 'moment';
import { Subject } from 'rxjs';

import { environment } from '../../../environments/environment';
import { FundingApi } from '../../shared/funding/funding-api.service';

@Component({
  selector: 'app-moneybar-config',
  templateUrl: 'moneybar-config.component.html',
  styleUrls: ['moneybar-config.component.scss'],
  standalone: false,
})
export class MoneybarConfigComponent implements OnInit {
  private static readonly QUERY_PARAM_PRESELECTED_GROUP = 'preselectGroupForDonation';

  @ViewChild(MoneybarContainerComponent) moneybar!: MoneybarContainerComponent;

  readonly rolesToEdit = [Permissions.ROLE_LEVEL_60];
  isProd = true;
  currentMoneybar!: Moneybar;
  noMoneybarsConfigured = false;
  settings!: Setting[];

  moneybarTimeTravelDate?: Moment | null;
  groupsReadyForDonations: BeneficiaryGroup[] = [];
  setGroupReadyForDonationsControl = new FormControl<BeneficiaryGroup | null>(null);
  groupsNotReadyForDonations: BeneficiaryGroup[] = [];
  private showSpinner = new Subject<boolean>();
  showSpinner$ = this.showSpinner.asObservable();

  editForm: UntypedFormGroup;
  totalFunding?: TotalFunding;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private beneficiaryGroupApi: BeneficiaryGroupApi,
    private settingApi: SettingApi,
    private fundingApi: FundingApi,
    private notificationService: NotificationService,
  ) {
    this.isProd = environment.production;
    this.editForm = formBuilder.group({});
  }

  ngOnInit(): void {
    this.loadCurrentMoneybar();
    this.settings = this.route.snapshot.data.settings;
    this.settings.sort(this.sortSettings);
    this.settings.forEach(setting => {
      if (setting.code) {
        this.editForm.addControl(
          setting.code,
          new UntypedFormControl(setting.value, {
            validators: [Validators.required],
          }),
        );
      }
    });

    const allRelevantGroups: BeneficiaryGroup[] = this.route.snapshot.data.allRelevantGroups;
    this.groupsReadyForDonations = allRelevantGroups
      .filter(g => g.readyForDonationsSequence !== undefined && g.readyForDonationsSequence !== null)
      .sort(this.beneficiaryGroupSortByReadyForDonationsSequenceFn);
    this.groupsNotReadyForDonations = allRelevantGroups.filter(
      g => g.readyForDonationsSequence === undefined || g.readyForDonationsSequence === null,
    );
    if (this.route.snapshot.queryParamMap.has(MoneybarConfigComponent.QUERY_PARAM_PRESELECTED_GROUP)) {
      this.setGroupReadyForDonationsControl.setValue(
        this.groupsNotReadyForDonations.find(
          group =>
            group.uuid === this.route.snapshot.queryParamMap.get(MoneybarConfigComponent.QUERY_PARAM_PRESELECTED_GROUP),
        ) || null,
      );
      this.setGroupReadyForDonationsControl.markAsDirty();
    }

    this.fundingApi.getTotalFunding().subscribe(result => (this.totalFunding = result));
  }

  settingDisplayFn(setting: Setting): string {
    switch (setting.code) {
      case 'yearlyDiscountRate':
        return 'Yearly discount rate (%)';
      case 'maxOvershoot':
        return 'Maximum overshoot (%)';
      default:
        return setting.code;
    }
  }

  beneficiaryGroupDisplayFn(group: BeneficiaryGroup | BeneficiaryGroup[] | undefined): string {
    if (!group) {
      return '';
    }

    if (group instanceof Array) {
      return '';
    }

    return `${group.code} (${group.status.description})`;
  }

  beneficiaryGroupSortFn(group1: BeneficiaryGroup, group2: BeneficiaryGroup): number {
    if (group1.status.displayOrder !== group2.status.displayOrder) {
      return (group1.status.displayOrder || 0) - (group2.status.displayOrder || 0);
    }

    return group1?.code && group2.code ? group2.code.localeCompare(group1.code) : -1;
  }

  beneficiaryGroupSortByReadyForDonationsSequenceFn(group1: BeneficiaryGroup, group2: BeneficiaryGroup): number {
    if (group1.readyForDonationsSequence !== undefined && group1.readyForDonationsSequence !== null) {
      if (group2.readyForDonationsSequence !== undefined && group2.readyForDonationsSequence !== null) {
        return group1.readyForDonationsSequence - group2.readyForDonationsSequence;
      } else {
        return 1;
      }
    } else if (group2.readyForDonationsSequence !== undefined && group2.readyForDonationsSequence !== null) {
      return -1;
    } else {
      return group1.code && group2.code ? group2.code.localeCompare(group1.code) : -1;
    }
  }

  sortSettings(setting1: Setting, setting2: Setting): number {
    return setting1 && setting1.code && setting2 && setting2.code ? setting1.code.localeCompare(setting2.code) : -1;
  }

  back(): void {
    this.location.back();
  }

  save(): void {
    const updatedSettings: Setting[] = [];
    this.settings.forEach(setting => {
      if (setting.code) {
        const updatedSetting = Object.assign({}, setting);
        updatedSetting.value = this.editForm.get(setting.code)?.value;
        updatedSettings.push(updatedSetting);
      }
    });
    this.settingApi.updateMoneybarSettings(updatedSettings).subscribe(() => {
      this.moneybar.reloadMoneybar();
    });
  }

  setGroupReadyForDonations(): void {
    const selectedGroup = this.setGroupReadyForDonationsControl.value;

    if (selectedGroup) {
      this.showSpinner.next(true);
      this.beneficiaryGroupApi.setGroupReadyForDonations(selectedGroup.uuid).subscribe({
        next: () => {
          this.noMoneybarsConfigured = false;
          this.groupsReadyForDonations.push(selectedGroup);
          this.groupsNotReadyForDonations.splice(this.groupsNotReadyForDonations.indexOf(selectedGroup), 1);
          this.setGroupReadyForDonationsControl.reset();
          this.setGroupReadyForDonationsControl.markAsPristine();
          this.showSpinner.next(false);
        },
        error: err => {
          this.notificationService.error(
            `Failed to select group ${selectedGroup.code} for a money bar: ${err.message}`,
            'Failed',
          );
          this.showSpinner.next(false);
        },
      });
    } else {
      this.notificationService.warning('Please select a group', 'No group selected');
    }
  }

  unsetGroupReadyForDonations(groupToRemove: BeneficiaryGroup): void {
    this.showSpinner.next(true);
    this.beneficiaryGroupApi.unsetGroupReadyForDonations(groupToRemove.uuid).subscribe({
      next: () => {
        this.groupsNotReadyForDonations.unshift(groupToRemove);
        this.groupsReadyForDonations.splice(this.groupsReadyForDonations.indexOf(groupToRemove), 1);
        this.showSpinner.next(false);
      },
      error: err => {
        this.notificationService.error(
          `Failed to remove group ${groupToRemove.code} for a money bar: ${err.message}`,
          'Failed',
        );
        this.showSpinner.next(false);
      },
    });
  }

  updateGroupReadyForDonationsSequence($event: CdkDragDrop<BeneficiaryGroup>): void {
    const draggedGroup: BeneficiaryGroup = $event.item.data;
    if (draggedGroup) {
      this.showSpinner.next(true);
      this.beneficiaryGroupApi.updateGroupReadyForDonationsSequence(draggedGroup.uuid, $event.currentIndex).subscribe({
        next: () => {
          moveItemInArray(this.groupsReadyForDonations, $event.previousIndex, $event.currentIndex);
          this.showSpinner.next(false);
          this.loadCurrentMoneybar();
        },
        error: err => {
          this.notificationService.error(
            `Failed to move group ${draggedGroup.code} for to position ${$event.currentIndex}: ${err.message}`,
            'Failed',
          );
          this.showSpinner.next(false);
        },
      });
    }
  }

  createNewGroup(): void {
    this.router.navigate(['/beneficiarygroups', 'new'], {
      queryParams: {
        callbackUrl: `${this.router.url.split('?')[0]}?${MoneybarConfigComponent.QUERY_PARAM_PRESELECTED_GROUP}=${BeneficiaryGroupEditComponent.UPDATED_GROUP_UUID_PLACEHOLDER}`,
      },
    });
  }

  moneybarTimeTravelTo($event: MatDatepickerInputEvent<Moment>): void {
    this.moneybarTimeTravelDate = $event.value;
  }

  private loadCurrentMoneybar(): void {
    this.fundingApi.getCurrentFunding().subscribe({
      next: result => {
        this.noMoneybarsConfigured = false;
        this.currentMoneybar = result;
      },
      error: (err: string) => {
        if (err && err.includes('404')) {
          this.noMoneybarsConfigured = true;
        }
      },
    });
  }
}
